import React, { useCallback, useState } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { ServiceRow } from "../logicBuilder/NewLogicModal";
import PlusIcon from "../../svg/PlusIcon";
import ActionButton from "../../../../common/ui/ActionButton";
import CustomAutoComplete from "./CustomAutoComplete";
import ServiceAutoComplete, { ServiceOption } from "./ServiceAutoComplete";
import { Box } from "@mui/material";
import BinSvg from "../../../../common/svgs/BinSvg";
import { enReportServiceType } from "../seviceBuilder/helper";
import SelectLabPanelModal from "../labBuilder/SelectLabPanelModal";

export interface SavedService {
  id: string | number;
  name: string;
  serviceType: number;
  active: number;
}

interface ServiceSelectionProps {
  serviceRows: ServiceRow[];
  setServiceRows: React.Dispatch<React.SetStateAction<ServiceRow[]>>;
  savedServices: SavedService[];
  savedLabPanels?: any[];
  title?: string;
  addButtonLabel?: string;
}

const ServiceSelection = ({
  serviceRows,
  setServiceRows,
  savedServices,
  savedLabPanels,
  title = "Service Outcomes",
  addButtonLabel = "Add service",
}: ServiceSelectionProps) => {
  const theme: Theme = useTheme();

  // Row whose "Select Lab Panel" modal is currently open (null = closed).
  const [openModalRowId, setOpenModalRowId] = useState<string | null>(null);

  // Get service type options from enReportServiceType
  // Only show "Lab panel" (type 8) when savedLabPanels is provided
  const serviceTypeOptions = Object.entries(enReportServiceType)
    .filter(([key]) => key !== "8" || !!savedLabPanels)
    .map(([, value]) => value);

  // Derive selected service IDs from serviceRows
  const selectedServiceIds = serviceRows
    .map((row) => row.selectedServiceId)
    .filter((id): id is string => id !== null);

  // Get filtered services for a specific row
  const getFilteredServices = useCallback(
    (rowType: string | null) => {
      if (!rowType) return [];

      // Convert display name back to service type number
      const serviceTypeKey = Object.keys(enReportServiceType).find(
        (key) => enReportServiceType[Number(key)] === rowType
      );

      if (!serviceTypeKey) return [];

      const serviceTypeNumber = Number(serviceTypeKey);

      // When type 8 (Lab panel) is selected and savedLabPanels are available,
      // show lab panels instead of services
      if (serviceTypeNumber === 8 && savedLabPanels) {
        return savedLabPanels
          .filter(
            (panel: any) => !selectedServiceIds.includes(panel.id.toString())
          )
          .map((panel: any) => ({
            id: panel.id,
            name: panel.name,
            serviceType: 8,
            active: 1,
          }));
      }

      // Filter services by type and exclude already selected services
      return savedServices
        .filter(
          (service) =>
            service.serviceType === serviceTypeNumber &&
            !selectedServiceIds.includes(service.id.toString())
        )
        .filter((s) => s.active === 1);
    },
    [savedServices, savedLabPanels, selectedServiceIds]
  );

  // Add a new row
  const handleAddRow = useCallback(() => {
    setServiceRows((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        selectedType: null,
        selectedServiceId: null,
      },
    ]);
  }, [setServiceRows]);

  // Delete a row
  const handleDeleteRow = useCallback(
    (rowId: string) => {
      setServiceRows((prev) => {
        // Don't delete if only one row exists
        if (prev.length === 1) {
          return prev;
        }

        // Simply filter out the row - selectedServiceIds will be derived from serviceRows
        return prev.filter((row) => row.id !== rowId);
      });
    },
    [setServiceRows]
  );

  // Update row type
  const handleTypeChange = useCallback(
    (rowId: string, type: string | null) => {
      setServiceRows((prev) =>
        prev.map((row) => {
          if (row.id === rowId) {
            // Check if selected type is "Lab panel" (type 8) and savedLabPanels are available
            const isLabPanel =
              !!savedLabPanels && type === enReportServiceType[8];
            // Reset selection when type changes
            return {
              ...row,
              selectedType: type,
              selectedServiceId: null,
              isLabPanel,
              labPanelData: undefined,
            };
          }
          return row;
        })
      );
      // Auto-open the Select Lab Panel modal as soon as "Lab panel" is chosen.
      if (!!savedLabPanels && type === enReportServiceType[8]) {
        setOpenModalRowId(rowId);
      }
    },
    [setServiceRows, savedLabPanels]
  );

  // Store the panel chosen in the modal on its row (id + full object).
  const handleLabPanelSelect = useCallback(
    (rowId: string, panel: any) => {
      setServiceRows((prev) =>
        prev.map((row) =>
          row.id === rowId
            ? {
                ...row,
                selectedServiceId: panel.id.toString(),
                labPanelData: panel,
              }
            : row
        )
      );
    },
    [setServiceRows]
  );

  // Update row service by ID (not name, since names can be duplicated)
  const handleServiceChange = useCallback(
    (rowId: string, serviceId: string | null) => {
      setServiceRows((prev) =>
        prev.map((row) => {
          if (row.id === rowId) {
            return { ...row, selectedServiceId: serviceId };
          }
          return row;
        })
      );
    },
    [setServiceRows]
  );

  return (
    <div style={styles.selectServicesWrapper(theme)}>
      <div style={styles.title(theme)}>{title}</div>
      {serviceRows.map((row) => {
        const filteredServices = getFilteredServices(row.selectedType);
        // Look up selected service (non-lab rows). Lab panel rows use the panel
        // object carried on the row (works for custom and provider panels).
        const selectedService = savedServices.find(
          (service) => service.id.toString() === row.selectedServiceId
        );

        // Lab-panel rows carry the chosen panel object on the row after a fresh
        // pick, but when editing a saved logic the row only has an id — resolve
        // the panel from the loaded list so the Name still shows.
        const resolvedLabPanel =
          row.labPanelData ??
          savedLabPanels?.find(
            (panel: any) => panel?.id?.toString() === row.selectedServiceId
          );

        // If there are no available services but this row has a selected service,
        // include that selected service in the options so it remains visible
        const serviceOptions =
          filteredServices.length === 0 && selectedService
            ? [selectedService]
            : filteredServices;

        const canDelete = serviceRows.length > 1;

        return (
          <div key={row.id} style={styles.serviceRow}>
            <CustomAutoComplete
              label="Type"
              options={serviceTypeOptions}
              value={row.selectedType}
              onChange={(value) => handleTypeChange(row.id, value)}
            />
            {row.isLabPanel ? (
              // Same outer flex wrapper as the ServiceAutoComplete branch below
              // so the lab-panel Name field has an identical width; the visual
              // field styling lives on the inner element.
              <Box sx={{ flex: 1, position: "relative", minWidth: 0 }}>
                <div
                  style={styles.labPanelField(theme)}
                  onClick={() => setOpenModalRowId(row.id)}
                  role="button"
                >
                  <span style={styles.labPanelLabel(theme)}>Name</span>
                  <span style={styles.labPanelValue(theme, !!resolvedLabPanel)}>
                    {resolvedLabPanel?.name || "Select lab panel"}
                  </span>
                </div>
              </Box>
            ) : (
              <Box sx={{ flex: 1, position: "relative", minWidth: 0 }}>
                <ServiceAutoComplete
                  label="Name"
                  options={serviceOptions as ServiceOption[]}
                  value={selectedService as ServiceOption | undefined}
                  onChange={(selected) => {
                    handleServiceChange(
                      row.id,
                      selected?.id.toString() ?? null
                    );
                  }}
                />
              </Box>
            )}
            <div
              onClick={() => canDelete && handleDeleteRow(row.id)}
              style={{
                cursor: canDelete ? "pointer" : "not-allowed",
                opacity: canDelete ? 1 : 0.5,
              }}
            >
              <BinSvg color={canDelete ? undefined : theme.colorTokens.N[60]} />
            </div>
          </div>
        );
      })}
      <SelectLabPanelModal
        open={openModalRowId !== null}
        onClose={() => setOpenModalRowId(null)}
        onSelect={(panel) => {
          if (openModalRowId) handleLabPanelSelect(openModalRowId, panel);
        }}
        excludeIds={serviceRows
          .filter(
            (row) =>
              row.id !== openModalRowId &&
              row.isLabPanel &&
              row.selectedServiceId
          )
          .map((row) => row.selectedServiceId as string)}
      />
      <ActionButton
        icon={<PlusIcon fill={theme.colors_ds.PRIMARY_ICON_MAIN} />}
        label={addButtonLabel}
        onClick={handleAddRow}
      />
    </div>
  );
};

export default ServiceSelection;

const styles = {
  selectServicesWrapper: (theme: Theme) => ({
    padding: "16px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
    alignItems: "flex-start",
  }),
  serviceRow: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
    width: "100%",
  },
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  labPanelField: (theme: Theme) => ({
    width: "100%",
    boxSizing: "border-box" as "border-box",
    position: "relative" as "relative",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    height: "56px",
    padding: "0 12px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    cursor: "pointer",
  }),
  labPanelLabel: (theme: Theme) => ({
    position: "absolute" as "absolute",
    top: "8px",
    left: "12px",
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
    lineHeight: "1",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  labPanelValue: (theme: Theme, hasValue: boolean) => ({
    flex: 1,
    marginTop: "12px",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "24px",
    color: hasValue
      ? theme.colors_ds.NEUTRAL_TEXT_STRONG
      : theme.colors_ds.NEUTRAL_TEXT_WEAK,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as "nowrap",
  }),
};
